import {
    assert,
    firstOrDie,
    indexOrDie,
    keyOrDie,
    mapValues,
    mustNeverExist,
    type Path,
    type Serializable,
    type SerializableObject,
    z,
} from "@repo/utils";
import type { UniversalAgentConfig, UniversalAgentNodeSchema, UniversalAgentNode } from "../types.js";

export function getNodeSchemaByValuePath(config: UniversalAgentConfig, valuePath: Path) {
    let current = {
        type: "object",
        nullable: false,
        description: "root",
        props: config.props,
    } as UniversalAgentNodeSchema;
    for (const part of valuePath) {
        switch (current.type) {
            case "string":
            case "number":
            case "boolean":
            case "source_id":
            case "enum":
            case "masterData":
            case "binding":
                throw new Error(`Can't got deeper to ${current.type} for a part ${part} of the path ${valuePath}`);
            case "array":
                current = {
                    type: "object",
                    description: "item",
                    nullable: true,
                    props: current.items,
                };
                break;
            case "object":
                current = keyOrDie(current.props, part).schema;
                break;
            default:
                throw mustNeverExist(current);
        }
    }

    return current;
}

export function buildDefaultValueBy(schema: UniversalAgentNodeSchema, root = true): Serializable {
    if (schema.nullable) {
        return null;
    }

    switch (schema.type) {
        case "string":
            return "";
        case "boolean":
            return false;
        case "number":
            return 0;
        case "enum":
            return firstOrDie(schema.values);
        case "masterData":
        case "binding":
        case "source_id":
            throw new Error(`Non-nullable ${schema.type} default value is not supported`);
        case "array":
            return root ? mapValues(schema.items, (_, value) => buildDefaultValueBy(value.schema, false)) : [];
        case "object":
            return mapValues(schema.props, (_, value) => buildDefaultValueBy(value.schema, false));
        default:
            throw mustNeverExist(schema);
    }
}

export function buildZodSchema(config: UniversalAgentConfig) {
    return buildPropsSchema(config.props);
}

function buildNodeSchema(node: UniversalAgentNode): z.ZodMiniType | null {
    const { schema } = node;
    let result: z.ZodMiniType;

    switch (schema.type) {
        case "string":
            result = z.string();
            break;
        case "number":
            result = z.number();
            break;
        case "object":
            result = buildPropsSchema(schema.props);
            break;
        case "array":
            result = z.array(buildPropsSchema(schema.items));
            break;
        case "boolean":
            result = z.boolean();
            break;
        case "source_id":
            result = z.string();
            break;
        case "enum":
            result = z.enum(schema.values);
            break;
        case "masterData":
            result = z.string();
            break;
        case "binding":
            return null;
        default:
            throw mustNeverExist(schema);
    }

    if (schema.nullable !== false) {
        result = z.nullable(result);
    }

    if (schema.description) {
        result = result.register(z.globalRegistry, { description: schema.description });
    }

    return result;
}

function buildPropsSchema(props: Record<string, UniversalAgentNode>) {
    const shape: Record<string, z.ZodMiniType> = {};
    for (const [key, node] of Object.entries(props)) {
        const schema = buildNodeSchema(node);
        if (schema !== null) {
            shape[key] = schema;
        }
    }
    return z.object(shape);
}

type WalkFn = (params: {
    value: Serializable | undefined;
    path: Path;
    schema: UniversalAgentNodeSchema;
}) => Promise<void>;

export async function walkSchemaAndValue(config: UniversalAgentConfig, data: Serializable, fn: WalkFn) {
    await walkInternal({ type: "object", description: "root", props: config.props, nullable: false }, data, [], fn);
}

async function walkInternal(schema: UniversalAgentNodeSchema, value: Serializable | undefined, path: Path, fn: WalkFn) {
    await fn({ value, path, schema });
    switch (schema.type) {
        case "string":
        case "number":
        case "boolean":
        case "source_id":
        case "enum":
        case "masterData":
        case "binding":
            break;
        case "array":
            if (value === null) {
                return;
            }
            assert(Array.isArray(value), `Expected an array on path ${path}. The actual value is ${value}`);
            for (let index = 0; index < value.length; index++) {
                const item = indexOrDie(value, index) as SerializableObject;
                for (const key in schema.items) {
                    await walkInternal(keyOrDie(schema.items, key).schema, item[key], [...path, index, key], fn);
                }
            }
            break;
        case "object":
            if (value === null) {
                return;
            }
            assert(typeof value === "object", `Expected an object on path ${path}. The actual value is ${value}`);
            for (const key in schema.props) {
                await walkInternal(
                    keyOrDie(schema.props, key).schema,
                    (value as SerializableObject)[key],
                    [...path, key],
                    fn,
                );
            }
            break;
        default:
            throw mustNeverExist(schema);
    }
}
